import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'
import { getCampaignSettingsQueryKey } from 'services/campaigns'

// Types
export interface SequenceStep {
  id: number
  order: number | null // null for variants; slot derives from variant_from_step_id
  email_subject: string
  email_body: string
  wait_in_days: number
  is_variant?: boolean
  variant_from_step_id?: number | null
  active?: boolean // whether the step/variant receives split traffic (EmailBison-owned)
  // When true the step replies within the prior step's email thread (carrying the
  // prior subject); when false it starts a new thread with its own subject. Step 1
  // is always false (it starts the thread). EmailBison-owned threading flag.
  // Optional because this type also models in-flight form state: a freshly added
  // step is `undefined` until the toggle resolves it. Readers normalize the gap
  // (`isThreadReplyOn ?? true` for display, `resolveThreadReplyForEmit ?? false`
  // for emit, `hydrateThreadReply ?? false` for persistence).
  thread_reply?: boolean
}

export interface Sequence {
  id: number
  title: string
  campaign_id: number
  bison_campaign_id: number // Note: This is now the same as the BisonCampaign.id
  steps: SequenceStep[]
  created_at: string
  updated_at: string
}

export interface CreateSequenceStepPayload {
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
  is_variant?: boolean
  // A new variant of a new original links by the original's order in the same
  // request; a new variant of an already-saved original links by its id. Exactly
  // one is set. See docs/solutions/emailbison-variant-write-contract.md.
  variant_from_step?: number | null
  variant_from_step_id?: number | null
  // Reply within the prior step's thread (true) vs start a new thread (false).
  // Step 1 is always false; backend coerces it as a hard invariant.
  thread_reply?: boolean
}

export interface UpdateSequenceStepPayload {
  id?: number // Bison step ID - required for existing steps, omit for new steps
  order: number
  email_subject: string
  email_body: string
  wait_in_days: number
  is_variant?: boolean
  variant_from_step?: number | null
  variant_from_step_id?: number | null
  // Reply within the prior step's thread (true) vs start a new thread (false).
  // Step 1 is always false; backend coerces it as a hard invariant.
  thread_reply?: boolean
}

export interface CreateSequencePayload {
  campaign_id: number
  title: string
  steps: CreateSequenceStepPayload[]
}

export interface UpdateSequencePayload {
  title?: string
  steps?: UpdateSequenceStepPayload[]
}

// Query keys
export const sequenceKeys = {
  all: ['sequences'] as const,
  byCampaign: (campaignId: number) =>
    [...sequenceKeys.all, 'campaign', campaignId] as const,
}

// API calls
const getSequenceByCampaign = async (
  campaignId: number
): Promise<Sequence | null> => {
  const response = await baseAPICall<Sequence[]>(() =>
    clientInstance.get('/emailbison/sequences/', {
      params: { campaign_id: campaignId },
    })
  )
  // API returns array, but we expect at most one sequence per campaign
  return response.length > 0 ? response[0] : null
}

const createSequence = async (
  payload: CreateSequencePayload
): Promise<Sequence> => {
  return baseAPICall<Sequence>(() =>
    clientInstance.post('/emailbison/sequences/', payload)
  )
}

const updateSequence = async (
  sequenceId: number,
  payload: UpdateSequencePayload
): Promise<Sequence> => {
  return baseAPICall<Sequence>(() =>
    clientInstance.put(`/emailbison/sequences/${sequenceId}/`, payload)
  )
}

const deleteSequence = async (sequenceId: number): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.delete(`/emailbison/sequences/${sequenceId}/`)
  )
}

// Hooks
export const useGetCampaignSequence = (campaignId: number, enabled = true) => {
  return useQuery({
    queryKey: sequenceKeys.byCampaign(campaignId),
    queryFn: () => getSequenceByCampaign(campaignId),
    enabled: enabled && campaignId > 0,
  })
}

export const useCreateSequence = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSequence,
    onSuccess: (data, variables) => {
      // Use input campaign_id (always correct) — response campaign_id can be null
      // when the reverse OneToOne hasn't resolved in the same transaction.
      const campaignId = data.campaign_id ?? variables.campaign_id
      queryClient.setQueryData(sequenceKeys.byCampaign(campaignId), data)
      queryClient.invalidateQueries({
        queryKey: sequenceKeys.byCampaign(campaignId),
      })
      queryClient.invalidateQueries({
        queryKey: getCampaignSettingsQueryKey({ campaign_id: campaignId }),
      })
    },
  })
}

export const useUpdateSequence = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sequenceId,
      payload,
    }: {
      sequenceId: number
      payload: UpdateSequencePayload
    }) => updateSequence(sequenceId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(sequenceKeys.byCampaign(campaignId), data)
      queryClient.invalidateQueries({
        queryKey: sequenceKeys.byCampaign(campaignId),
      })
    },
  })
}

export const useDeleteSequence = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSequence,
    onSuccess: () => {
      queryClient.setQueryData(sequenceKeys.byCampaign(campaignId), null)
    },
  })
}

async function setSequenceStepActive(
  stepId: number,
  active: boolean
): Promise<SequenceStep> {
  return baseAPICall<SequenceStep>(() =>
    clientInstance.patch(`/emailbison/sequence-steps/${stepId}/active/`, {
      active,
    })
  )
}

// Activate/deactivate a variant step. Invalidates the campaign sequence so the
// UI reflects the confirmed server state (no optimistic update).
export const useSetSequenceStepActive = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ stepId, active }: { stepId: number; active: boolean }) =>
      setSequenceStepActive(stepId, active),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: sequenceKeys.byCampaign(campaignId),
      })
    },
  })
}

export interface SendStepTestEmailPayload {
  sender_email_id: number
  to_email: string
  use_dedicated_ips?: boolean
  /**
   * When previewing a step for a specific contact, the caller pre-substitutes
   * variables and passes the resolved subject + body. The backend routes these
   * through Bison's compose endpoint instead of the random-lead test-email,
   * so the test inbox shows exactly what this contact would receive.
   */
  rendered_subject?: string
  rendered_body?: string
  plain_text?: boolean
  recipient_name?: string
}

interface SendStepTestEmailArgs {
  bisonStepId: number
  payload: SendStepTestEmailPayload
}

const sendStepTestEmail = async ({
  bisonStepId,
  payload,
}: SendStepTestEmailArgs): Promise<{ success: boolean; message: string }> => {
  return baseAPICall<{ success: boolean; message: string }>(() =>
    clientInstance.post(
      `/emailbison/sequence-steps/${bisonStepId}/test-email/`,
      payload
    )
  )
}

export const useSendStepTestEmail = () => {
  return useMutation({ mutationFn: sendStepTestEmail })
}
